import { MouseEvent, ChangeEvent } from 'react';
import { BlindTestListState } from './BlindTestListTypes';
import { setLocalStorageItem } from '../../../../Services/Global';

/**
 * Factory function to create page change handler
 */
export const createHandlePageChange = (
  state: BlindTestListState,
  getDesignSessionData: (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => Promise<void>
) => {
  return async (event: MouseEvent<HTMLButtonElement> | null, page: number) => {
    if (state.unitInputConfiguration && Object.keys(state.unitInputConfiguration).length > 0) {
      setLocalStorageItem(state.pageKeyName, page.toString());
    }
    getDesignSessionData(state.currentFilter, page);
  };
};

/**
 * Factory function to create rows per page change handler
 */
export const createHandleChangeRowsPerPage = (
  state: BlindTestListState,
  getDesignSessionData: (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => Promise<void>
) => {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    state.setRowsPerPage(newRowsPerPage);
    state.setCurrentPage(0);
    
    if (state.unitInputConfiguration && Object.keys(state.unitInputConfiguration).length > 0) {
      setLocalStorageItem(state.pageKeyName, "0");
    }
    
    // Pass the new rows per page directly to ensure immediate update
    getDesignSessionData(state.currentFilter, 0, newRowsPerPage);
  };
};

/**
 * Factory function to create column filter change handler
 */
export const createHandleColumnFilterChange = (
  state: BlindTestListState,
  fetchAllPagesData: (query?: string) => void,
  getDesignSessionData: (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => Promise<void>
) => {
  return (columnId: string, value: string) => {
    state.setFilterColumn(columnId);
    state.setFilterValue(value);
    
    // If filter value is empty, clear filter
    if (!value.trim()) {
      state.setCurrentFilter('');
      getDesignSessionData('', state.currentPage);
      return;
    }
    
    // Create filter in format supported by backend
    const query = `&filter=${columnId}:${value}`;
    state.setCurrentFilter(query);
    
    // When filtering, get all data across all pages
    fetchAllPagesData(query);
  };
};

/**
 * Factory function to create sort menu click handler
 */
export const createHandleSortMenuClick = (
  state: BlindTestListState,
  fetchAllPagesData: (query?: string) => void
) => {
  return (direction: 'asc' | 'desc' | null) => {
    if (direction === null) {
      state.setOrderBy('');
    } else {
      state.setOrderBy(state.activeColumn);
      state.setOrder(direction);
      
      // Only fetch all data if we don't already have filtered data
      if (!state.filterValue) {
        fetchAllPagesData();
      }
    }
    state.setColumnMenuAnchor(null);
  };
};

/**
 * Factory function to create column menu handlers
 */
export const createColumnMenuHandlers = (state: BlindTestListState) => {
  const handleOpenColumnMenu = (event: React.MouseEvent<HTMLButtonElement>, columnId: string) => {
    event.stopPropagation();
    state.setColumnMenuAnchor(event.currentTarget);
    state.setActiveColumn(columnId);
  };

  const handleCloseColumnMenu = () => {
    state.setColumnMenuAnchor(null);
  };

  const handleOpenColumnManager = () => {
    state.setColumnManagerOpen(true);
    state.setColumnMenuAnchor(null);
  };

  const handleCloseColumnManager = () => {
    state.setColumnManagerOpen(false);
  };

  const handleToggleColumnVisibility = (columnId: string) => {
    if (state.hiddenColumns.includes(columnId)) {
      state.setHiddenColumns(state.hiddenColumns.filter(id => id !== columnId));
    } else {
      state.setHiddenColumns([...state.hiddenColumns, columnId]);
    }
    state.setColumnMenuAnchor(null);
  };

  return {
    handleOpenColumnMenu,
    handleCloseColumnMenu,
    handleOpenColumnManager,
    handleCloseColumnManager,
    handleToggleColumnVisibility
  };
};

/**
 * Factory function to create filter toggle handler
 */
export const createHandleToggleFilters = (state: BlindTestListState) => {
  return () => {
    state.setShowFilters(!state.showFilters);
  };
};

/**
 * Factory function to create navigation handlers
 */
export const createNavigationHandlers = (navigate: (path: string, options?: any) => void) => {
  const handleNavigateBack = () => {
    navigate('/blindtests/dashboard');
  };

  const handleNavigateToDetail = (item: any, unitInputConfiguration: any, configurations: any[]) => {
    navigate("/DesignSessionReviewForm", { 
      state: { 
        unitConfiguration: unitInputConfiguration, 
        designSession: item, 
        multiSectionConfiguration: configurations 
      } 
    });
  };

  return {
    handleNavigateBack,
    handleNavigateToDetail
  };
}; 